import { BrowserRouter, Routes, Route, useLocation, useParams } from 'react-router-dom'
import { NavHeader } from './components/NavHeader'
import AnalyzePage from './pages/AnalyzePage'
import ResultsPage from './pages/ResultsPage'
import HistoryPage from './pages/HistoryPage'
import LoginPage from './pages/LoginPage'
import ListenPage from './pages/ListenPage'
import { AuthProvider } from './contexts/AuthContext'
import { EmbeddedProvider } from './contexts/EmbeddedContext'
import { KnktBackgroundOrbs } from './components/ui/knkt-background'
import './components/ui/knkt-background.css'
import './App.css'

/**
 * Hide the Audio Agent NavHeader on the public ListenPage routes. Labels
 * land there from a Pitch demo email — they don't have a KNKT account and
 * shouldn't see "Upload track" / "Sign in" Audio Agent chrome. The
 * ListenPage renders its own KNKT Listen branded TopHeader instead.
 */
function ConditionalNavHeader() {
  const { pathname } = useLocation();
  const onListenRoute =
    pathname.startsWith('/listen/') ||
    // Neutral domain — single-segment path that isn't one of the known UI
    // routes is a track id (listen.knkt.io/<id> form).
    /^\/[^/]+$/.test(pathname) &&
      !['/login', '/history', '/results', ''].includes(pathname);
  if (onListenRoute) return null;
  return <NavHeader />;
}

/**
 * Wrapper that lifts `audioFileId` out of either route shape so ListenPage
 * works for both `/listen/:audioFileId` and the neutral `/:audioFileId`
 * pattern shipped on listen.knkt.io. The component itself reads via
 * useParams, so it Just Works for both — this is just an explicit boundary
 * so future routing changes don't quietly break the listen page.
 */
function ListenPageRoute() {
  const { audioFileId } = useParams();
  if (!audioFileId) return null;
  return <ListenPage />;
}

function App() {
  return (
    <EmbeddedProvider>
      <AuthProvider>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <div className="knkt-background" aria-hidden="true">
            <KnktBackgroundOrbs />
            <div className="knkt-noise-overlay" />
            <div className="knkt-grid-overlay" />
          </div>
          <div style={{ position: 'relative', zIndex: 1 }}>
            <ConditionalNavHeader />
            <Routes>
              <Route path="/" element={<AnalyzePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/history" element={<HistoryPage />} />
              <Route path="/results/:analysisId" element={<ResultsPage />} />
              {/*
                Public — no AuthProvider gate inside, no NavHeader chrome.
                Labels click an email shortlink and land here. The ListenPage
                fetches its own metadata via the public /api/listen endpoint
                and fires engagement events to pitch.knkt.io/api/listen-events.
              */}
              <Route path="/listen/:audioFileId" element={<ListenPageRoute />} />
              {/* Neutral domain alias: listen.knkt.io/<id>?via=… ships from
                  Pitch without the "/listen/" prefix. React Router's
                  best-match keeps the literal routes above intact; anything
                  unmatched + single-segment routes here. */}
              <Route path="/:audioFileId" element={<ListenPageRoute />} />
            </Routes>
          </div>
        </BrowserRouter>
      </AuthProvider>
    </EmbeddedProvider>
  )
}

export default App
